import { useCallback, useMemo, useState } from 'react';
import {
  buildSevereWeatherAlertAreas,
  findSevereWeatherAlertAreaById,
  getSevereWeatherAlertIdWithinArea,
  getStableSevereWeatherAlertId,
} from '../utils/severeWeatherAlerts.utils';

/**
 * Prepares severe-weather map areas and owns the currently selected area and alert.
 *
 * API fetching stays with each page or widget. This hook only keeps the shared selection behavior
 * consistent when refreshed alert objects replace the previous response.
 */
const useSevereWeatherAlertSelection = (alerts = []) => {
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [selectedAlertId, setSelectedAlertId] = useState(null);

  const alertAreas = useMemo(() => buildSevereWeatherAlertAreas(alerts), [alerts]);
  const selectedArea = useMemo(
    () => findSevereWeatherAlertAreaById(alertAreas, selectedAreaId),
    [alertAreas, selectedAreaId],
  );
  const selectedAlert = useMemo(() => {
    if (!selectedArea) return null;

    return (
      selectedArea.alerts.find(
        (alert, alertIndex) => getStableSevereWeatherAlertId(alert, alertIndex) === selectedAlertId,
      ) ??
      selectedArea.primaryAlert ??
      null
    );
  }, [selectedAlertId, selectedArea]);

  const selectArea = useCallback((area) => {
    if (!area) return;

    setSelectedAreaId(area.id);
    setSelectedAlertId(getSevereWeatherAlertIdWithinArea(area, area.primaryAlert));
  }, []);

  const selectAreaById = useCallback(
    (areaId) => selectArea(findSevereWeatherAlertAreaById(alertAreas, areaId)),
    [alertAreas, selectArea],
  );

  const selectAlert = useCallback(
    (alert) => setSelectedAlertId(getSevereWeatherAlertIdWithinArea(selectedArea, alert)),
    [selectedArea],
  );

  return {
    alertAreas,
    selectedArea,
    selectedAlert,
    selectedAlertId: getSevereWeatherAlertIdWithinArea(selectedArea, selectedAlert),
    selectArea,
    selectAreaById,
    selectAlert,
  };
};

export default useSevereWeatherAlertSelection;
